// V2-round-3 — landing-page motion: stat count-up + carousel auto-scroll.
//
// Two pieces:
//  1. Count-up: any element with data-count-to="N" (and optional
//     data-count-suffix) animates from 0 → N when it enters the viewport.
//  2. Carousel auto-scroll: every .v2-row__track auto-scrolls every 4s,
//     pauses on hover/focus/touch, loops back to start when reaching end.
//     Honours prefers-reduced-motion.

const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ─── Count-up ──────────────────────────────────────────────────────────
function animateCountUp(el: HTMLElement, to: number, suffix: string, durationMs = 1400) {
  if (REDUCED_MOTION) {
    el.textContent = String(to) + suffix;
    return;
  }
  const start = performance.now();
  const from = 0;
  function frame(now: number) {
    const t = Math.min(1, (now - start) / durationMs);
    // ease-out cubic
    const e = 1 - Math.pow(1 - t, 3);
    const v = Math.round(from + (to - from) * e);
    el.textContent = String(v) + suffix;
    if (t < 1) requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

function attachCountUp() {
  const targets = Array.from(document.querySelectorAll<HTMLElement>("[data-count-to]"));
  if (targets.length === 0) return;
  if (!("IntersectionObserver" in window)) {
    // Fallback: just set the final value.
    targets.forEach((el) => {
      const to = parseInt(el.dataset.countTo || "0", 10);
      const suffix = el.dataset.countSuffix || "";
      el.textContent = String(to) + suffix;
    });
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      const el = e.target as HTMLElement;
      const to = parseInt(el.dataset.countTo || "0", 10);
      const suffix = el.dataset.countSuffix || "";
      animateCountUp(el, to, suffix);
      io.unobserve(el);
    });
  }, { threshold: 0.5 });
  targets.forEach((el) => io.observe(el));
}

// ─── Carousel auto-scroll ──────────────────────────────────────────────
// Continuous (self-moving) horizontal scroll using requestAnimationFrame —
// the previous setInterval+scrollTo behaviour stepped one card per 4s on
// click-like cadence; this version creeps at ~40 px/s, pauses on hover/
// focus/touch, loops seamlessly when it reaches the end.
function attachAutoScroll() {
  if (REDUCED_MOTION) return;
  const tracks = Array.from(document.querySelectorAll<HTMLElement>(".v2-row__track"));
  const PX_PER_SECOND = 40;  // gentle creep; raise for faster movement
  tracks.forEach((track) => {
    let paused = false;
    let hidden = false;
    let lastTs = 0;
    let rafId = 0;
    let userScrollPauseUntil = 0;

    function tick(ts: number) {
      if (lastTs === 0) lastTs = ts;
      const dt = ts - lastTs;
      lastTs = ts;
      const now = performance.now();
      if (!paused && !hidden && now > userScrollPauseUntil) {
        const max = track.scrollWidth - track.clientWidth;
        if (max > 1) {
          let next = track.scrollLeft + (PX_PER_SECOND * dt) / 1000;
          if (next >= max - 0.5) next = 0;  // seamless loop to start
          track.scrollLeft = next;
        }
      }
      rafId = requestAnimationFrame(tick);
    }

    track.addEventListener("mouseenter", () => { paused = true;  });
    track.addEventListener("mouseleave", () => { paused = false; });
    track.addEventListener("focusin",    () => { paused = true;  });
    track.addEventListener("focusout",   () => { paused = false; });
    track.addEventListener("touchstart", () => { paused = true;  }, { passive: true });
    track.addEventListener("touchend",   () => { setTimeout(() => { paused = false; }, 6000); }, { passive: true });
    // If the user manually scrolls (wheel/touchpad), give them 4s grace
    // before resuming the creep so we don't fight the input.
    track.addEventListener("wheel", () => { userScrollPauseUntil = performance.now() + 4000; }, { passive: true });

    document.addEventListener("visibilitychange", () => {
      hidden = document.hidden;
      lastTs = 0;
    });

    rafId = requestAnimationFrame(tick);
    // Surface for potential teardown in future SPA-style nav (no-op today).
    (track as any)._v2AutoScrollId = rafId;
  });
}

function attach() {
  attachCountUp();
  attachAutoScroll();
}

if (document.readyState !== "loading") attach();
else document.addEventListener("DOMContentLoaded", attach);
